import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { devApi, prodDevApi } from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';

interface ICreatePermanentVAccountRequest {
  requestReference: string;
  accountName: string;
  autoPayoutEnabled?: boolean;
}

interface ICreatePermanentVAccountResponse {
  status?: string;
  message?: string;
  statusCode?: string;
  errors?: IApiResponseError[];
}

const initialState: IApiState<ICreatePermanentVAccountResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const createPermanentVAccountAsync = createAsyncThunk(
  'permanentVAccount/create',
  async (
    values: ICreatePermanentVAccountRequest,
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const devAccessToken = state.devAuth.value?.data?.token?.accessToken;

      const devTokenInStorage = localStorage.getItem('devTokenData');

      const parsedDevTokenInStorage = devTokenInStorage
        ? JSON.parse(devTokenInStorage)
        : null;

      const developerId = localStorage.getItem('developerId');

      const environment = state.environment?.value;

      const apiInstance = environment === 'live' ? prodDevApi : devApi;

      const authToken =
        environment === 'live' ? accessToken : parsedDevTokenInStorage;

      const config = {
        headers: {
          Authorization: `Token ${authToken}`,
          'Business-id': developerId,
          Environment: 'live',
        },
      };

      const { data } = await apiInstance.post<ICreatePermanentVAccountResponse>(
        '/permanent-virtual-accounts',
        values,
        config
      );

      if (devAccessToken) {
        localStorage.setItem('devTokenData', JSON.stringify(devAccessToken));
      }

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? 'Virtual Account created successfully',
          type: 'drawer',
        })
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<ICreatePermanentVAccountResponse>;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
      });

      dispatch(
        showAlert({
          status: 'error',
          description: errorMessage,
          type: 'drawer',
        })
      );

      return rejectWithValue(error.response?.data);
    }
  }
);

export const permanentVAccountCreationSlice = createSlice({
  name: 'permanentVAccount/create',
  initialState,
  reducers: {
    resetPermanentVAccountCreation(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createPermanentVAccountAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(createPermanentVAccountAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as ICreatePermanentVAccountResponse;
      })
      .addCase(createPermanentVAccountAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as ICreatePermanentVAccountResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetPermanentVAccountCreation } =
  permanentVAccountCreationSlice.actions;

export default permanentVAccountCreationSlice.reducer;
